import { Spinner as FluentSpinner, SpinnerSize } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import { timeToShowSpinner } from "../../shared/constants";
import { useComponentLogger } from "../../shared/logging";
import { BaseProps, validateProps } from "../../shared/props";

export type AriaLive = "assertive" | "polite" | "off";

export type LabelPosition = "top" | "right" | "left" | "bottom";

export { SpinnerSize };

export interface SpinnerProps extends BaseProps {
    /**
     * Aria label for Spinner.
     */
    readonly "aria-label": string;
    /**
     * Pliteness setting for label update annoucement. "assertive" | "polite" | "off". Default is 'polite'.
     */
    readonly "aria-live"?: AriaLive;
    /**
     * Text that would be shown with Spinner.
     */
    readonly label?: string;
    /**
     * Position of label from spinner. "top" | "right" | "left" | "bottom"
     */
    readonly labelPosition?: LabelPosition;
    /**
     * Size of spinner. "xSmall" | "small" | "medium" | "large"
     */
    readonly spinnerSize?: SpinnerSize;
}

export const Spinner: React.FC<SpinnerProps> = ({
    label,
    labelPosition = "right",
    spinnerSize = SpinnerSize.medium,
    ...otherProps
}) => {
    useComponentLogger("Spinner", otherProps.id);

    validateProps("Spinner", otherProps);

    return (
        <FluentSpinner
            role="alert"
            label={label}
            labelPosition={labelPosition}
            size={spinnerSize}
            {...otherProps}
        />
    );
};

//Updating spinner shown in progress views for other components
export const UpdatingLoadingSpinner: React.FC = () => {
    const [showSpinner, setShowSpinner] = useState(false);

    useEffect(() => {
        // Do not show Spinner right away, wait for a while before showing
        // spinner. This is designed for better user experience.
        const timeoutID = setTimeout(() => {
            setShowSpinner(true);
        }, timeToShowSpinner);

        return () => clearTimeout(timeoutID);

    }, [/* We only run once to register the time out */]);

    return showSpinner ? <FluentSpinner /> : null;
};
